import { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { USER_CREATION_ALLOWED_EMAIL, MAX_YAZILIMCI_USERS } from '../constants';

// Rol sabitleri
export const ROLES = {
  ADMIN: 'admin',
  SATISCI: 'satisci',
  YAZILIMCI: 'yazilimci'
};

// Rol yetkileri
export const PERMISSIONS = {
  [ROLES.ADMIN]: {
    canAccessDashboard: true,
    canAccessCustomers: true,
    canAccessProducts: true,
    canEditProducts: true,
    canManageUsers: true,
    canSeeAllData: true,
    canCreateCustomer: true,
    canEditCustomer: true,
    canDeleteCustomer: true,
    canCreateOrder: true,
    canEditOrder: true,
    canDeleteOrder: true,
    canUpdateOrderStatus: true,
  },
  [ROLES.SATISCI]: {
    canAccessDashboard: false,
    canAccessCustomers: true,
    canAccessProducts: true,
    canEditProducts: false,
    canManageUsers: false,
    canSeeAllData: true,
    canCreateCustomer: true,
    canEditCustomer: true,
    canDeleteCustomer: true,
    canCreateOrder: true,
    canEditOrder: true,
    canDeleteOrder: true,
    canUpdateOrderStatus: true,
  },
  [ROLES.YAZILIMCI]: {
    canAccessDashboard: true,
    canAccessCustomers: true,
    canAccessProducts: true,
    canEditProducts: true,
    canManageUsers: true,
    canSeeAllData: false,
    canCreateCustomer: true,
    canEditCustomer: true,
    canDeleteCustomer: true,
    canCreateOrder: true,
    canEditOrder: true,
    canDeleteOrder: true,
    canUpdateOrderStatus: true,
    onlyOwnData: true,
  },
};

export const normalizeUserRole = (role) => {
  if (role === 'montajci') return ROLES.SATISCI;
  if (Object.values(ROLES).includes(role)) return role;
  return ROLES.SATISCI;
};

export const normalizeAccountEmail = (email) => email?.toLowerCase().trim() || '';

export const isAuthorizedSystemAccountEmail = (email) =>
  normalizeAccountEmail(email) === normalizeAccountEmail(USER_CREATION_ALLOWED_EMAIL);

export const canAssignYazilimciRole = (email, existingUsers = [], targetUserId = null) => {
  if (!isAuthorizedSystemAccountEmail(email)) return false;
  const yazilimciCount = existingUsers.filter(
    (u) => u.role === ROLES.YAZILIMCI && u.id !== targetUserId
  ).length;
  return yazilimciCount < MAX_YAZILIMCI_USERS;
};

export const canCreateSystemUsers = (user, userRole) => {
  if (userRole === ROLES.ADMIN || userRole === ROLES.YAZILIMCI) return true;
  return false;
};

export const getCreatableRoles = (user, userRole, existingUsers = [], targetEmail = '') => {
  if (userRole === ROLES.ADMIN || userRole === ROLES.YAZILIMCI) {
    const roles = [ROLES.SATISCI];
    const yazilimciCount = existingUsers.filter((u) => u.role === ROLES.YAZILIMCI).length;
    if (yazilimciCount < MAX_YAZILIMCI_USERS && isAuthorizedSystemAccountEmail(targetEmail)) {
      roles.push(ROLES.YAZILIMCI);
    }
    roles.push(ROLES.ADMIN);
    return roles;
  }

  return [];
};

export const getYazilimciRoleHint = (targetEmail = '') => {
  if (!targetEmail) {
    return 'Yazılımcı rolü yalnızca kahramantechinfo@gmail.com adresine atanabilir.';
  }
  if (!isAuthorizedSystemAccountEmail(targetEmail)) {
    return 'Bu e-posta yazılımcı rolü için uygun değil. kahramantechinfo@gmail.com kullanın.';
  }
  return null;
};

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);

        try {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();

            if (userData.disabled === true) {
              await signOut(auth);
              setUser(null);
              setUserRole(null);
              setPermissions(null);
              setLoading(false);
              return;
            }

            const role = normalizeUserRole(userData.role || ROLES.SATISCI);
            setUserRole(role);
            setPermissions(PERMISSIONS[role] || PERMISSIONS[ROLES.SATISCI]);
          } else {
            const defaultRole = ROLES.ADMIN;

            await setDoc(userDocRef, {
              email: firebaseUser.email,
              role: defaultRole,
              displayName: firebaseUser.displayName || firebaseUser.email.split('@')[0],
              createdAt: new Date(),
              createdBy: 'system',
            });

            setUserRole(defaultRole);
            setPermissions(PERMISSIONS[defaultRole]);
          }
        } catch (error) {
          console.error('Kullanıcı rolü alınamadı:', error);
          setUserRole(ROLES.SATISCI);
          setPermissions(PERMISSIONS[ROLES.SATISCI]);
        }
      } else {
        setUser(null);
        setUserRole(null);
        setPermissions(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const hasPermission = (permission) => permissions?.[permission] || false;

  const isRole = (role) => userRole === role;

  return {
    user,
    userRole,
    permissions,
    loading,
    hasPermission,
    isRole,
    isAdmin: userRole === ROLES.ADMIN,
    isSatisci: userRole === ROLES.SATISCI,
    isYazilimci: userRole === ROLES.YAZILIMCI,
    canCreateUsers: canCreateSystemUsers(user, userRole),
  };
};
